// Адмін: фідбек користувачів — середня оцінка, розподіл зірок, список голосів.
// Оцінки прилітають із розсилок бота (зірочки в повідомленні, fb:<n>:<source>).
import { useState } from 'react';
import { api } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

export function AdminFeedback() {
  const [source, setSource] = useState<string | undefined>(undefined);
  const { data, loading, error, reload } = useFetch(() => api.admin.feedback(source), [source]);

  return (
    <div className="space-y-4 p-4">
      <h2 className="text-lg font-semibold">Фідбек користувачів</h2>

      {loading && <Loader />}
      {error && <ErrorView error={error} onRetry={reload} />}

      {data && (
        <>
          {/* Фільтр за кампанією */}
          {data.sources.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <FilterChip active={!source} onClick={() => setSource(undefined)}>
                Всі ({data.sources.reduce((a, s) => a + s.count, 0)})
              </FilterChip>
              {data.sources.map((s) => (
                <FilterChip
                  key={s.source}
                  active={source === s.source}
                  onClick={() => setSource(s.source)}
                >
                  {s.source} ({s.count})
                </FilterChip>
              ))}
            </div>
          )}

          {/* Зведення */}
          <section className="card">
            <div className="flex items-center gap-4">
              <div>
                <div className="text-3xl font-bold text-yellow-500">
                  {data.stats.avg.toFixed(2)}
                </div>
                <div className="text-xs text-tg-hint">{data.stats.total} оцінок</div>
              </div>
              <div className="flex-1 space-y-1">
                {[5, 4, 3, 2, 1].map((r) => {
                  const count = data.stats.dist[r] ?? 0;
                  const pct = data.stats.total > 0 ? (count / data.stats.total) * 100 : 0;
                  return (
                    <div key={r} className="flex items-center gap-2 text-xs">
                      <span className="w-6 text-right text-tg-hint">{r}★</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-tg-bg">
                        <div className="h-full bg-yellow-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-tg-hint">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Список голосів */}
          {data.items.length === 0 ? (
            <p className="card text-sm text-tg-hint">
              Поки що жодної оцінки. Фідбек з'явиться після розсилки із зірочками.
            </p>
          ) : (
            <section className="space-y-2">
              {data.items.map((f) => (
                <div key={f.id} className="card flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">
                      {f.user.firstName ?? 'Без імені'}
                      {f.user.username && (
                        <span className="ml-1 text-tg-hint">@{f.user.username}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-tg-hint">
                      {f.source} · {new Date(f.updatedAt).toLocaleString('uk-UA')}
                      {f.user.email && ` · ${f.user.email}`}
                    </div>
                    {f.comment && <div className="mt-1 text-sm">{f.comment}</div>}
                  </div>
                  <div className="shrink-0 text-sm text-yellow-500">
                    {'★'.repeat(f.rating)}
                    <span className="text-tg-hint">{'★'.repeat(5 - f.rating)}</span>
                  </div>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
        active ? 'bg-tg-button text-tg-button-text' : 'bg-tg-secondary-bg text-tg-text'
      }`}
    >
      {children}
    </button>
  );
}
